import React, { useEffect, useState } from 'react';
import { drService } from './drService';
import { 
  PageHeader, 
  MetricCard, 
  SectionCard, 
  ConfirmDialog, 
  StatusBadge,
  LoadingState,
  ErrorState
} from '../../components/common';
import { theme } from '../../design/theme';

export const DisasterRecoveryPanel: React.FC = () => {
  const [status, setStatus] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    type: 'freeze' | 'failover' | null;
    title: string;
    description: string;
    isCritical: boolean;
  }>({
    isOpen: false,
    type: null,
    title: '',
    description: '',
    isCritical: false
  });

  const fetchDRStatus = async () => {
    try {
      const drStatus = await drService.getDRStatus();
      setStatus(drStatus);
    } catch (err: any) {
      console.error('Failed to fetch DR status', err);
      // Let the ErrorState handle it if status is null
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDRStatus();
  }, []);

  const handleActionConfirm = async (justification: string) => {
    if (justification.length < 10) {
      alert('Justification must be at least 10 characters.');
      return;
    }

    try {
      if (confirmDialog.type === 'freeze') {
        const nextState = !status.is_frozen;
        await drService.toggleFinancialFreeze(nextState, justification);
      } else if (confirmDialog.type === 'failover') {
        await drService.triggerFailover(justification);
      }
      
      setConfirmDialog({ ...confirmDialog, isOpen: false });
      fetchDRStatus();
    } catch (err: any) {
      alert('Action failed: ' + (err.message || 'Error'));
    }
  };

  if (loading) return <LoadingState />;
  if (!status) return <ErrorState message="Failed to load DR control plane data" />;

  return (
    <div>
      <PageHeader 
        title="Disaster Recovery & Financial Safety" 
        subtitle="Critical control plane for global system overrides and failover."
      />
      
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(3, 1fr)', 
        gap: theme.spacing.lg,
        marginBottom: theme.spacing.xl
      }}>
        <MetricCard 
          label="REPLICA LAG" 
          value={`${status.replica_lag_seconds}s`} 
          status={status.replica_lag_healthy ? 'healthy' : 'warning'} 
        />
        <MetricCard 
          label="FINANCIAL FREEZE" 
          value={status.is_frozen ? 'ACTIVE' : 'INACTIVE'} 
          status={status.is_frozen ? 'critical' : 'healthy'} 
        />
        <MetricCard 
          label="RECONCILIATION" 
          value={status.reconciliation_mismatch ? 'MISMATCH' : 'OK'} 
          status={status.reconciliation_mismatch ? 'critical' : 'healthy'} 
        />
      </div>

      <SectionCard title="Safety Overrides" subtitle="Authorized high-stakes actions only. All actions are logged with justification.">
        <div style={{ display: 'flex', gap: theme.spacing.lg }}>
          <div style={{ flex: 1, ...theme.card, padding: theme.spacing.lg, border: `1px solid ${theme.colors.neutral[200]}` }}>
            <div style={{ marginBottom: theme.spacing.md }}>
              <h4 style={{ ...theme.typography.h3, marginBottom: theme.spacing.xs }}>Global Financial Freeze</h4>
              <p style={{ ...theme.typography.caption }}>Immediately suspends all financial transactions across the platform.</p>
            </div>
            <button 
              onClick={() => setConfirmDialog({
                isOpen: true,
                type: 'freeze',
                title: status.is_frozen ? 'Deactivate Global Freeze' : 'Activate Global Freeze',
                description: 'This will affect all active tenants and ongoing transactions. Please confirm you have authority for this action.',
                isCritical: !status.is_frozen
              })}
              style={{
                ...theme.typography.bodyBold,
                backgroundColor: status.is_frozen ? theme.colors.healthy.main : theme.colors.critical.main,
                color: 'white',
                border: 'none',
                padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              {status.is_frozen ? 'Deactivate Global Freeze' : 'Activate Global Freeze'}
            </button>
          </div>

          <div style={{ flex: 1, ...theme.card, padding: theme.spacing.lg, border: `1px solid ${theme.colors.neutral[200]}` }}>
            <div style={{ marginBottom: theme.spacing.md }}>
              <h4 style={{ ...theme.typography.h3, marginBottom: theme.spacing.xs }}>Regional Failover</h4>
              <p style={{ ...theme.typography.caption }}>Promotes standby replica to primary. Expect 30-60s of read-only mode.</p>
            </div>
            <button 
              disabled={status.replica_lag_seconds > 300}
              onClick={() => setConfirmDialog({
                isOpen: true,
                type: 'failover',
                title: 'Trigger Regional Failover',
                description: 'This is an IRREVERSIBLE action that will promote the standby database. All current connections will be dropped.',
                isCritical: true
              })}
              style={{
                ...theme.typography.bodyBold,
                backgroundColor: theme.colors.neutral[900],
                color: 'white',
                border: 'none',
                padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
                borderRadius: '4px',
                cursor: 'pointer',
                opacity: status.replica_lag_seconds > 300 ? 0.5 : 1
              }}
            >
              Trigger Regional Failover
            </button>
            {status.replica_lag_seconds > 300 && (
              <p style={{ ...theme.typography.caption, color: theme.colors.critical.main, marginTop: theme.spacing.sm }}>
                Failover blocked: Replica lag exceeds RPO threshold.
              </p>
            )}
          </div>
        </div>
      </SectionCard>

      <ConfirmDialog 
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        description={confirmDialog.description}
        isCritical={confirmDialog.isCritical}
        onConfirm={handleActionConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
      />
    </div>
  );
};
